import { supabase } from '../lib/supabase';

export interface Report {
    id: string;
    reporter_id: string;
    reported_user_id: string;
    reason: string;
    description: string;
    severity: 'low' | 'medium' | 'high' | 'critical';
    status: 'pending' | 'reviewing' | 'resolved' | 'dismissed';
    moderation_action: 'none' | 'warning' | 'content_removed' | 'temporary_ban' | 'permanent_ban';
    content_type: 'user' | 'post' | 'comment';
    content_id?: string;
    created_at: string;
    updated_at: string;
    admin_notes?: string;
    resolved_by?: string;
    resolved_at?: string;
    report_source?: string;
    content_preview?: string;
    additional_details?: string;
    reporter?: {
        display_name: string;
        email: string;
        photo_url: string;
        location?: string;
        state?: string;
        city?: string;
    };
    reported_user?: {
        display_name: string;
        email: string;
        photo_url: string;
        location?: string;
        state?: string;
        city?: string;
    };
}

export interface ReportStats {
    pending: number;
    resolved: number;
    dismissed: number;
    highSeverity: number;
}

class ReportService {
    async getReports(status: string = 'pending'): Promise<Report[]> {
        try {
            // Fetch reports with user details
            // Note: Supabase joins might be tricky if relations aren't perfect, 
            // but we'll try standard joins first.
            const { data, error } = await supabase
                .from('reports')
                .select(`
          *,
          reporter:users!reporter_id(display_name, email, photo_url, location),
          reported_user:users!reported_user_id(display_name, email, photo_url, location)
        `)
                .eq('status', status)
                .order('created_at', { ascending: false });

            if (error) throw error;
            return data || [];
        } catch (error) {
            console.error('Error fetching reports:', error);
            throw error;
        }
    }

    async getStats(): Promise<ReportStats> {
        try {
            const { count: pending } = await supabase
                .from('reports')
                .select('*', { count: 'exact', head: true })
                .eq('status', 'pending');

            const { count: resolved } = await supabase
                .from('reports')
                .select('*', { count: 'exact', head: true })
                .eq('status', 'resolved');

            const { count: dismissed } = await supabase
                .from('reports')
                .select('*', { count: 'exact', head: true })
                .eq('status', 'dismissed');

            const { count: highSeverity } = await supabase
                .from('reports')
                .select('*', { count: 'exact', head: true })
                .in('severity', ['high', 'critical'])
                .eq('status', 'pending');

            return {
                pending: pending || 0,
                resolved: resolved || 0,
                dismissed: dismissed || 0,
                highSeverity: highSeverity || 0
            };
        } catch (error) {
            console.error('Error fetching report stats:', error);
            return { pending: 0, resolved: 0, dismissed: 0, highSeverity: 0 };
        }
    }

    async updateReportStatus(
        id: string,
        status: 'pending' | 'reviewing' | 'resolved' | 'dismissed',
        action: string = 'none'
    ): Promise<void> {
        try {
            const { error } = await supabase
                .from('reports')
                .update({
                    status,
                    moderation_action: action,
                    updated_at: new Date().toISOString()
                })
                .eq('id', id);

            if (error) throw error;
        } catch (error) {
            console.error('Error updating report status:', error);
            throw error;
        }
    }

    async updateAdminNotes(id: string, notes: string): Promise<void> {
        try {
            const { error } = await supabase
                .from('reports')
                .update({ admin_notes: notes })
                .eq('id', id);

            if (error) throw error;
        } catch (error) {
            console.error('Error updating admin notes:', error);
            throw error;
        }
    }



    async hideContent(type: 'post' | 'comment', contentId: string, reason: string): Promise<void> {
        try {
            const table = type === 'post' ? 'posts' : 'post_comments';

            const { error } = await supabase
                .from(table)
                .update({
                    is_hidden: true,
                    hidden_reason: reason
                })
                .eq('id', contentId);

            if (error) throw error;
        } catch (error) {
            console.error(`Error hiding ${type}:`, error);
            throw error;
        }
    }

    // Helper to fetch the actual content content for preview
    async getContentDetails(type: 'post' | 'comment', contentId: string): Promise<any> {
        try {
            if (type === 'post') {
                const { data } = await supabase
                    .from('posts')
                    .select('caption, media_url, media_type')
                    .eq('id', contentId)
                    .single();
                return data;
            } else if (type === 'comment') {
                const { data } = await supabase
                    .from('post_comments')
                    .select('text')
                    .eq('id', contentId)
                    .single();
                return data;
            }
            return null;
        } catch (error) {
            console.error('Error fetching content details:', error);
            return null;
        }
    }
}

const reportService = new ReportService();
export default reportService;
